import { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import {
  selectCurrentNavigationLevel,
  selectNavigationScope,
} from "../store/reducers/workspaceReducer";
import { LoadingStatus, NavigationLevel } from "@nendaTypes/NendaTypes";
import {
  getCompanyGroups,
  selectGroupsByNavigationLevelWithInheritance,
} from "../store/reducers/groupReducer";
import { Box, Button, Chip, Paper, Stack, Typography } from "@mui/material";
import PageTitle from "../ui-components/typography/PageTitle";
import DefaultDataGrid from "../ui-components/dataGrid/DefaultDataGrid";
import {
  bulkSetGroup,
  getScreens,
  getScreensByCompanyId,
  resetUpdateStatus,
  selectSignageScreensForScope,
} from "../store/reducers/screenReducer";
import { GridRowSelectionModel } from "@mui/x-data-grid";
import { CustomerPortalState } from "../store";
import { t } from "i18next";
import { BusinessOutlined, LocationOnOutlined } from "@mui/icons-material";
import { selectPremisesForScope } from "../store/reducers/organizationUnitReducer";

const Group: React.FC = () => {
  const { groupId, parentId } = useParams<{
    groupId: string;
    parentId: string;
  }>();

  const [didFetchScreens, setDidFetchScreens] = useState(false);
  const [selection, setSelection] = useState<GridRowSelectionModel>([]);
  const [initialIds, setInitialIds] = useState<Set<string>>(new Set());

  const dispatch = useDispatch();

  const navigationScope = useSelector(selectNavigationScope);
  const scopeLevel = useSelector(selectCurrentNavigationLevel);
  const groups = useSelector(selectGroupsByNavigationLevelWithInheritance);
  const screens = useSelector(selectSignageScreensForScope);
  const premises = useSelector(selectPremisesForScope);
  const updateStatus = useSelector(
    (state: CustomerPortalState) => state.screen.updateStatus
  );

  useEffect(() => setDidFetchScreens(false), [parentId]);

  useEffect(() => {
    if (
      scopeLevel !== NavigationLevel.PREMISE &&
      scopeLevel !== NavigationLevel.COMPANY
    )
      return;
    if (screens.length === 0 && !didFetchScreens) {
      setDidFetchScreens(true);
      if (scopeLevel === NavigationLevel.COMPANY) {
        if (parentId) dispatch(getScreensByCompanyId(parentId));
      } else if (scopeLevel === NavigationLevel.PREMISE) {
        if (parentId) dispatch(getScreens(parentId));
      }
    }
  }, [dispatch, scopeLevel, screens.length, parentId, didFetchScreens]);

  useEffect(() => {
    if (updateStatus === LoadingStatus.SUCCEEDED) {
      setInitialIds(new Set(selection as string[]));
      setSelection([]);
      dispatch(resetUpdateStatus());
      if (scopeLevel === NavigationLevel.COMPANY) {
        if (parentId) dispatch(getScreensByCompanyId(parentId));
      } else if (scopeLevel === NavigationLevel.PREMISE) {
        if (parentId) dispatch(getScreens(parentId));
      }
    }
    if (updateStatus === LoadingStatus.FAILED) {
      dispatch(resetUpdateStatus());
    }
  }, [updateStatus]);

  useEffect(() => {
    if (navigationScope.company) {
      dispatch(getCompanyGroups(navigationScope.company));
    }
  }, [navigationScope.company]);

  const group = groups.find((g) => g._id === groupId);
  useEffect(() => {
    if (!group || screens.length === 0) return;

    const idsInGroup = new Set(
      screens.filter((s) => s.groups.includes(group._id)).map((s) => s._id)
    );

    setInitialIds(idsInGroup);
    setSelection(Array.from(idsInGroup));
  }, [group, screens]);

  const diff = useMemo(() => {
    const current = new Set(selection as string[]);

    const toAdd = Array.from(current).filter((id) => !initialIds.has(id));
    const toRemove = Array.from(initialIds).filter((id) => !current.has(id));

    return {
      toAdd,
      toRemove,
      hasChanges: toAdd.length > 0 || toRemove.length > 0,
    };
  }, [selection, initialIds]);

  function handleSave() {
    if (!diff.hasChanges) return;
    dispatch(bulkSetGroup({ groupId, add: diff.toAdd, remove: diff.toRemove }));
  }

  const sortedScreens = useMemo(() => {
    return [...screens].sort((a, b) => {
      const preSelected = [...initialIds];
      const aSelected = preSelected.includes(a._id);
      const bSelected = preSelected.includes(b._id);

      if (aSelected && !bSelected) return -1;
      if (!aSelected && bSelected) return 1;

      return a.hotel.localeCompare(b.hotel);
    });
  }, [screens, selection]);

  const rows = useMemo(
    () =>
      sortedScreens.map((screen) => ({
        id: screen._id,
        name: screen.name,
        groups: screen.groups,
        premise: premises.find((p) => p._id === screen.hotel)?.name,
      })),
    [sortedScreens, groups]
  );

  const columns = useMemo(
    () => [
      {
        field: "name",
        headerName: t("customerportal.pages.group.table.name") || "Name",
        flex: 1,
      },
      {
        field: "premise",
        headerName: t("customerportal.pages.group.table.premise") || "Premise",
        flex: 1,
      },
      {
        field: "groups",
        headerName:
          t("customerportal.pages.group.table.current_group") || "Group",
        flex: 1,
        renderCell: (params) => {
          return (
            <Stack
              direction="row"
              flexWrap="wrap"
              gap={0.5}
              sx={{
                whiteSpace: "normal",
                overflow: "visible",
                width: "100%",
              }}
            >
              {(params.value as string[]).map((gid) => {
                const group = groups.find((g) => g._id === gid);
                if (!group) return null;

                return (
                  <Chip
                    key={gid}
                    label={group.name}
                    size="small"
                    color={group.premise ? "primary" : "secondary"}
                    icon={
                      group.premise ? (
                        <LocationOnOutlined fontSize="small" />
                      ) : (
                        <BusinessOutlined fontSize="small" />
                      )
                    }
                  />
                );
              })}
            </Stack>
          );
        },
      },
    ],
    [groups]
  );

  if (!group) {
    return null;
  }

  if (
    scopeLevel === NavigationLevel.PREMISE &&
    group.premise &&
    group.premise !== navigationScope.premise
  ) {
    return null;
  }

  return (
    <Box>
      <PageTitle title={group.name} />
      <Typography
        sx={{ paddingBottom: "1rem", maxWidth: "40rem" }}
        variant="body1"
      >
        {t("customerportal.pages.group.helper_text")}
      </Typography>
      <DefaultDataGrid
        rows={rows}
        columns={columns}
        checkboxSelection
        disableRowSelectionOnClick
        rowSelectionModel={selection}
        onRowSelectionModelChange={setSelection}
        loading={updateStatus === LoadingStatus.LOADING}
        getRowHeight={() => "auto"}
        sx={{
          "& .MuiDataGrid-cell": {
            py: 1,
          },
        }}
      />
      {diff.hasChanges && (
        <Paper
          elevation={3}
          sx={{
            position: "sticky",
            bottom: 0,
            zIndex: 5,
            p: 2,
            display: "flex",
            gap: 2,
            alignItems: "center",
          }}
        >
          <Typography variant="body2">{selection.length} selected</Typography>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={updateStatus === LoadingStatus.LOADING}
          >
            Save
          </Button>
        </Paper>
      )}
    </Box>
  );
};

export default Group;
